"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { Block, BlockType, uuid } from "../types/screenplay-editor.types";
import { Camera, Shot, ShotPart } from "../types/shot-breakdown.types";

export interface UseShotBreakdownOptions {
  scene_url: string;
  fetchOptions?: RequestInit;
  onAISummarize?: (scene: any) => void;
  onShotAdded?: (shot: Shot) => any;
  onShotsBulkAdded?: (shots: Shot[], scene_type: string) => Promise<void>;
  onShotRemoved?: (shotId: string) => any;
  onShotUpdated?: (shotId: string, detail: Shot) => any;
  onProductionInitialized?: (
    cameras: Camera[],
    type: string,
    dance_choreographer_required?: boolean,
    action_sequence_required?: boolean,
  ) => any;
  preloadedShots?: Shot[];
  preloadedCameras?: Camera[];
  preloadedSceneType?: string;
}

export function useShotBreakdownScene(options: UseShotBreakdownOptions) {
  const [shots, setShots] = useState<Shot[]>(options.preloadedShots || []);
  const [cameras, setCameras] = useState<Camera[]>(
    options.preloadedCameras || [],
  );
  const [sceneType, setSceneType] = useState<string>(
    options.preloadedSceneType || "",
  );
  const [scene, setScene] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSummarizing, setIsSummarizing] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [selectionMenu, setSelectionMenu] = useState<{
    top: number;
    left: number;
    parts: ShotPart[];
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (options.preloadedShots && options.preloadedShots.length > 0) {
      setShots((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(options.preloadedShots)) {
          return prev;
        }
        return options.preloadedShots || [];
      });
    }
  }, [options.preloadedShots]);

  useEffect(() => {
    if (options.preloadedCameras && options.preloadedCameras.length > 0) {
      setCameras((prev) => {
        if (JSON.stringify(prev) === JSON.stringify(options.preloadedCameras)) {
          return prev;
        }
        return options.preloadedCameras || [];
      });
    }
  }, [options.preloadedCameras]);

  useEffect(() => {
    if (options.preloadedSceneType) {
      setSceneType((prev) => {
        if (prev === options.preloadedSceneType) return prev;
        return options.preloadedSceneType || "";
      });
    }
  }, [options.preloadedSceneType]);

  useEffect(() => {
    setIsLoading(true);
    const fetchScene = async () => {
      try {
        const response = await fetch(options.scene_url, options.fetchOptions);
        if (response.ok) {
          const text = await response.text();
          setScene({ content: text });
        } else {
          console.error("Failed to fetch scene:", response);
          setError(true);
        }
        setIsLoading(false);
      } catch (error) {
        setError(true);
        setIsLoading(false);
        console.error("Error fetching scene:", error);
      }
    };
    fetchScene();
  }, []);

  const blocks = useMemo(() => {
    if (!scene || !scene.content) return [];

    const parser = new DOMParser();
    const doc = parser.parseFromString(scene.content, "text/html");
    const divs = Array.from(doc.querySelectorAll("div"));
    const parsedBlocks: Block[] = [];

    const typeMap: Record<string, BlockType> = {
      divtype0: "SCENE_HEADING",
      divtype2: "ACTION",
      divtype3: "CHARACTER",
      divtype4: "PARENTHETICAL",
      divtype5: "DIALOGUE",
      divtype6: "TRANSITION",
    };

    divs.forEach((div) => {
      const divText = div.textContent?.trim() || "";
      if (!divText) return;

      let type: BlockType = "ACTION";
      for (const className of Array.from(div.classList)) {
        if (typeMap[className]) {
          type = typeMap[className];
          break;
        }
      }

      const idAttr = div.getAttribute("id");
      const blockId =
        idAttr && idAttr.startsWith("par")
          ? idAttr.substring(3)
          : idAttr || uuid();

      parsedBlocks.push({ id: blockId, type, text: divText });
    });

    return parsedBlocks;
  }, [scene]);

  const clearSelection = () => {
    setSelectionMenu(null);
    window.getSelection()?.removeAllRanges();
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        clearSelection();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getAbsoluteOffset = (
    blockEl: Element,
    targetNode: Node,
    targetOffset: number,
  ) => {
    let currentOffset = 0;
    const walker = document.createTreeWalker(
      blockEl,
      NodeFilter.SHOW_TEXT,
      null,
    );
    let currentNode = walker.nextNode();
    while (currentNode) {
      if (currentNode === targetNode) {
        return currentOffset + targetOffset;
      }
      currentOffset += currentNode.nodeValue?.length || 0;
      currentNode = walker.nextNode();
    }
    return targetOffset;
  };

  const handleMouseUp = () => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    const startNode = range.startContainer;
    const endNode = range.endContainer;

    const startBlockEl =
      startNode.nodeType === Node.TEXT_NODE
        ? startNode.parentElement?.closest("[data-block-id]")
        : (startNode as Element).closest("[data-block-id]");
    const endBlockEl =
      endNode.nodeType === Node.TEXT_NODE
        ? endNode.parentElement?.closest("[data-block-id]")
        : (endNode as Element).closest("[data-block-id]");

    if (!startBlockEl || !endBlockEl) return;

    const startBlockId = startBlockEl.getAttribute("data-block-id");
    const endBlockId = endBlockEl.getAttribute("data-block-id");

    const startIndexInArray = blocks.findIndex((b) => b.id === startBlockId);
    const endIndexInArray = blocks.findIndex((b) => b.id === endBlockId);

    if (startIndexInArray === -1 || endIndexInArray === -1) return;

    const startAbsOffset = getAbsoluteOffset(
      startBlockEl,
      startNode,
      range.startOffset,
    );
    const endAbsOffset = getAbsoluteOffset(
      endBlockEl,
      endNode,
      range.endOffset,
    );

    const selectedParts: ShotPart[] = [];

    for (let i = startIndexInArray; i <= endIndexInArray; i++) {
      const block = blocks[i];
      const start = i === startIndexInArray ? startAbsOffset : 0;
      const end = i === endIndexInArray ? endAbsOffset : block.text.length;

      if (start !== end) {
        selectedParts.push({
          block_id: block.id,
          start_index: start,
          end_index: end,
          text: block.text.slice(start, end),
        });
      }
    }

    if (selectedParts.length > 0) {
      const rect = range.getBoundingClientRect();
      setSelectionMenu({
        top: rect.top + window.scrollY,
        left: rect.left + rect.width / 2 + window.scrollX,
        parts: selectedParts,
      });
    }
  };

  const initializeProduction = async (
    count: number,
    type: string,
    dance_choreographer_required?: boolean,
    action_sequence_required?: boolean,
  ) => {
    const newCameras: Camera[] = [];
    for (let i = 1; i <= count; i++) {
      newCameras.push({ name: `Camera ${String.fromCharCode(64 + i)}` });
    }

    const result = options.onProductionInitialized?.(
      newCameras,
      type,
      dance_choreographer_required,
      action_sequence_required,
    );
    if (result instanceof Promise) {
      setCameras(newCameras);
      setSceneType(type);
      await result;
    }

    return result;
  };

  const addShot = (shotDetails: Omit<Shot, "id" | "shot_number" | "parts">) => {
    if (!selectionMenu) return;
    const newShot: Shot = {
      ...shotDetails,
      id: uuid(),
      shot_number: shots.length + 1,
      parts: selectionMenu.parts,
    };
    setShots((prev) => [...prev, newShot]);

    const result = options.onShotAdded?.(newShot);
    if (result instanceof Promise) {
      return result.then(() => {
        clearSelection();
      });
    } else {
      clearSelection();
      return result;
    }
  };

  const updateShot = async (
    shotId: string | number,
    updatedDetails: Omit<Shot, "id" | "shot_number" | "parts">,
  ) => {
    let updatedShot: Shot | null = null;
    setShots((prev) =>
      prev.map((shot) => {
        if (shot.id === shotId) {
          updatedShot = { ...shot, ...updatedDetails };
          return updatedShot;
        }
        return shot;
      }),
    );

    if (updatedShot) {
      const result = options.onShotUpdated?.(shotId as string, updatedShot);
      if (result instanceof Promise) {
        await result;
      }
      return result;
    }
  };

  const handleAISummarize = async () => {
    if (!scene || !scene.content || !options.onAISummarize) return;
    setIsSummarizing(true);
    try {
      const res: any = await options.onAISummarize?.(scene.content);

      if (res && res.ok) {
        const data = await res.json();
        setIsSummarizing(false);

        const newShots: Shot[] = [];
        data.data[0].forEach((aiShot: any) => {
          // Map AI response to Shot type
          // This mapping depends on what the AI returns for shots
          const newShot: Shot = {
            id: aiShot.id || uuid(),
            shot_number: (shots.length || 0) + newShots.length + 1,
            priority: aiShot.priority || "Essential",
            shot_type: aiShot.shot_type || "Medium Shot (MS)",
            framing: aiShot.framing || "Eye Level",
            camera_angle: aiShot.camera_angle || "Eye Level",
            camera_movement: aiShot.camera_movement || "Static (Locked-Off)",
            camera_support: aiShot.camera_support || "Tripod",
            lens_mm: aiShot.lens_mm || "35",
            lens_feel: aiShot.lens_feel || "Standard",
            aperture: aiShot.aperture || "T2.8",
            depth_of_field: aiShot.depth_of_field || "Deep",
            subject: aiShot.subject || "",
            action_blocking: aiShot.action_blocking || "",
            emotional_purpose: aiShot.emotional_purpose || "",
            lighting: aiShot.lighting || "",
            sound: aiShot.sound || "",
            duration_seconds: aiShot.duration_seconds || 5,
            edit_purpose: aiShot.edit_purpose || "",
            reason: aiShot.reason || "",
            vfx: aiShot.vfx || "None",
            camera_name: aiShot.camera_name || cameras[0]?.name || "Camera A",
            fps: aiShot.fps || 24,
            parts:
              aiShot.parts.map((part: any) => ({
                block_id: part.block_id.startsWith("par")
                  ? part.block_id.substring(3)
                  : part.block_id || uuid(),
                start_index: part.start_index,
                end_index: part.end_index,
                text: part.text,
              })) || [],
          };
          newShots.push(newShot);
        });

        if (newShots.length > 0) {
          setShots((prev) => [...prev, ...newShots]);
          if (options.onShotsBulkAdded) {
            await options.onShotsBulkAdded(newShots, data.data[1]);
          }
        }
      } else {
        setIsSummarizing(false);
      }
    } catch (error) {
      setIsSummarizing(false);
      console.error("Error in AI summarization:", error);
    }
  };

  return {
    blocks,
    isLoading,
    error,
    shots,
    sceneType,
    selectionMenu,
    handleMouseUp,
    cameras,
    initializeProduction,
    addShot,
    updateShot,
    clearSelection,
    handleAISummarize,
    isSummarizing,
    menuRef,
  };
}
